/* istanbul ignore file */
import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { get, isEmpty } from "lodash";
import { Button, Spinner, ToolTips } from "@px/px_design_system";
import PropTypes from "prop-types";
import queryString from "query-string";

import DisplayError from "../../DisplayError";
import {
  AdApprovalListContext,
  AdApprovalListProvider,
} from "../../context/AdApprovalContext";
import { UserContext } from "../../context/auth";
import { LaunchAdApprovalIcon } from "../../svgs/AdApprovalIcon";
import PrepareSvg from "../../svgs/PrepareSvg";
import {
  prepareCsvData,
  showCustomTooltip,
  getUTCDateTime,
  fixedEncodeURIComponent,
  replaceAllCharacter,
} from "../../../../utils";
import PaginatedDownloadCsv from "../Utilities/PaginatedDownloadCsv";
import ToolTipMessages from "../Utilities/ToolTip/TooltipMessages";
import { Table } from "../Utilities/Table";
import Headline from "../Headline";
import adApprovalModel from "../../models/AdApproval";

import "./style.css";

const AdApprovalListComponent = (props) => {
  const { organization } = useContext(UserContext);
  const [finalAdApprovals, setFinalAdApprovals] = useState([]);
  const [searchResultCount, setSearchResultCount] = useState(0);
  const { history, location } = props;

  const {
    isLoaded,
    statusArray,
    fetchCsvData,
    csvColumnFormation,
    creativeIcon,
    totalAdApprovals,
    tabName,
  } = useContext(AdApprovalListContext);

  const createLinkToTableDomain = (cell) => {
    let cellValue = cell;
    if (typeof cell !== "string") {
      cellValue = cell.toString();
    }

    const cellArray = cellValue.split(", ");
    const cellArrayLength = cellArray.length;
    const links = cellArray.map((link, index) => (
      <React.Fragment key={cellArrayLength + link}>
        <Link key={cellArray.length++} to={`//${link}`} target="_blank">
          {link}
        </Link>
        {cellArrayLength - 1 !== index && ", "}
      </React.Fragment>
    ));
    return !isEmpty(cell) ? links : "-";
  };

  const launchAdApprovalProps = {
    style: {
      backgroundColor: "#2D6BFF",
      borderColor: "#2D6BFF",
      color: "#ffffff",
    },
    buttontext: "Launch Approval Queue",
    icon: <PrepareSvg iconComponent={LaunchAdApprovalIcon} />,
  };

  const csvDataToDownload = (adApprovalData) => {
    const listData = adApprovalData.map((item, index) =>
      csvColumnFormation(item, index)
    );
    const params = {
      list: listData,
      fileFormat: "csv",
      modelObj: adApprovalModel,
    };
    const getCsvData = prepareCsvData(params);
    setFinalAdApprovals(getCsvData);
  };

  const spinnerProps = {
    className: "spinner-icon",
    color: "primary",
    loadingtext: "Loading",
  };

  const appendParams = (auditStatus) => {
    const values = queryString.parse(props?.history?.location.search);
    const params = `?audit__status=${auditStatus}&ordering=${values.ordering}`;
    return !isEmpty(values.search)
      ? `${params}&search=${values.search}`
      : params;
  };

  const navigateToPath = (appendUrl) => {
    appendUrl = replaceAllCharacter(appendUrl, "%", "%25");
    const auditStatus = queryString.parse(location.search).audit__status;
    history.push({
      pathname: `/orgs/${organization.id}/adapprovals/${fixedEncodeURIComponent(
        appendUrl
      )}`,
      search: appendParams(auditStatus),
    });
  };

  const createLinkToTableId = (cell, row) => {
    const approvalName = get(row.original, "name", "");
    return (
      <Link
        to={`../adapprovals/${fixedEncodeURIComponent(
          replaceAllCharacter(approvalName, "%", "%25")
        )}${appendParams(statusArray.toString())}`}
        data-index={row.id}
        className={`link${row.id}`}
      >
        {cell}
      </Link>
    );
  };

  const fallbackValue = (cell) => {
    return !isEmpty(cell) ? cell : "-";
  };

  let remoteColumns = [
    {
      accessorKey: "ad.name",
      header: "DSP Creative ID",
      dataField: "ad__name",
      Cell: ({ cell, row }) => {
        return createLinkToTableId(cell.getValue(), row);
      },
    },
    {
      accessorKey: "ad.ext.owned_by_name",
      header: "DSP",
      dataField: "ad__organization__name",
      Cell: ({ cell }) => {
        return fallbackValue(cell.getValue());
      },
    },
    {
      accessorKey: "ad.adomain",
      header: "Advertiser Domain",
      dataField: "ad__adomain",
      Cell: ({ cell }) => {
        return createLinkToTableDomain(cell.getValue());
      },
    },
    {
      accessorKey: "ad.creative.ext.type",
      header: "Ad Type",
      dataField: "ad__creative__kind",
      Cell: ({ cell, row }) => {
        return creativeIcon(cell.getValue(), row);
      },
    },
    {
      accessorKey: "ad.lang_display",
      header: "Language",
      dataField: "ad__lang_display",
      Cell: ({ cell }) => {
        return fallbackValue(cell.getValue());
      },
    },
    {
      accessorKey: "id",
      header: "Ad Approval ID",
      dataField: "id",
      Cell: ({ cell }) => {
        return fallbackValue(cell.getValue());
      },
    },
    {
      accessorKey: "ts",
      header: "Date Created",
      dataField: "ts",
      Cell: ({ cell }) => {
        return showCustomTooltip(
          getUTCDateTime(cell.getValue()),
          "Time shown in UTC"
        );
      },
    },
    {
      accessorKey: "audit.lastmod",
      header: "Last Modified",
      dataField: "adapprovalaudit__lastmod",
      Cell: ({ cell }) => {
        return showCustomTooltip(
          getUTCDateTime(cell.getValue()),
          "Time shown in UTC"
        );
      },
    },
  ];

  return (
    <>
      <div
        className={`ad--approval-list wrapper ${
          isLoaded ? "wrapper--no-border" : ""
        }`}
      >
        {isLoaded && <Spinner {...spinnerProps} />}
        {!isLoaded && (
          <div className="ad--approval-container">
            <h2 className="deal__headline deal__headline--copy sub__headline--count">
              <>{tabName}</>
            </h2>
            <Table
              table_columns={remoteColumns}
              orgId={organization.id}
              name="adapprovals"
              extraUrlParams={[
                {
                  key: "status",
                  value:
                    statusArray.toString() !== "all"
                      ? statusArray.toString()
                      : "",
                },
              ]}
              history={history}
              defaultSort={[
                {
                  id: "audit.lastmod",
                  desc: true,
                },
              ]}
              setSearchResultCount={setSearchResultCount}
              renderTopToolbarCustomActions={() => {
                return (
                  <div className="buttons">
                    <PaginatedDownloadCsv
                      componentName="AdApprovals"
                      fetchData={fetchCsvData}
                      csvDataToDownload={csvDataToDownload}
                      disableDownloadCsv={totalAdApprovals === 0}
                      disableDownloadText={
                        totalAdApprovals === 0
                          ? ToolTipMessages.DOWNLOAD_CSV.title
                          : ""
                      }
                      finalData={finalAdApprovals}
                      removeColumns={["serial"]}
                      filename={tabName}
                      renameHeaders={{
                        ad__name: "dsp_creative_id",
                        ad__organization__name: "dsp",
                        ad__adomain: "advertiser_domain",
                        ad__creative__kind: "ad_type",
                        ad__lang_display: "language",
                        id: "ad_approval_id",
                        ts: "created",
                        adapprovalaudit__lastmod: "lastmod",
                      }}
                      key={tabName}
                      dataSize={totalAdApprovals}
                    />
                    <ToolTips
                      className=""
                      disabled={
                        totalAdApprovals === 0 || searchResultCount === 0
                      }
                      text={
                        totalAdApprovals === 0 || searchResultCount === 0
                          ? ToolTipMessages.LAUNCH_APPROVAL_NO_DATA.title
                          : ""
                      }
                    >
                      <Button
                        type="button"
                        id="launchApprovalQueueId"
                        {...launchAdApprovalProps}
                        onClick={() => navigateToPath("queue")}
                        className="launch--button"
                        disabled={
                          totalAdApprovals === 0 || searchResultCount === 0
                        }
                      />
                    </ToolTips>
                  </div>
                );
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

AdApprovalListComponent.propTypes = {
  children: PropTypes.node,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  computedMatch: PropTypes.shape({
    params: PropTypes.object,
  }),
  location: PropTypes.shape({
    push: PropTypes.func,
    search: PropTypes.string,
  }),
};

const AdApprovalList = (props) => {
  const { match } = props;
  const { data, errorMessage, loading } = useContext(UserContext);
  return (
    <>
      <Headline>Ad Approvals</Headline>
      {!isEmpty(data) && !data.orgData.errors && !loading ? (
        <AdApprovalListProvider
          key={match.params.id}
          {...props}
          isAdApprovalDetails={false}
        >
          <AdApprovalListComponent {...props} />
        </AdApprovalListProvider>
      ) : (
        <DisplayError {...errorMessage} />
      )}
    </>
  );
};

AdApprovalList.propTypes = {
  match: PropTypes.shape(),
};

export const AdApprovalListProviders = AdApprovalListComponent;
export default AdApprovalList;
